<template>
  <div class="education-modal">
    <v-dialog
      v-model="isVisible"
      max-width="566"
      transition="dialog-transition"
    >
      <v-card>
        <v-card-title
          class="education-modal__title headline"
          primary-title
        >
          EDUCATION
          <v-btn icon large color="primary" @click="closeModal">
            <v-icon>mdi-close</v-icon>
          </v-btn>
        </v-card-title>

        <v-card-text class="pb-0">
          <v-form ref="educationForm" v-model="isFormValid" @submit.prevent="submitForm">
            <template v-if="selectedEducation.type === 'university'">
              <v-row class="education-modal__row">
                <v-col class="education-modal__col">
                  <v-text-field
                    v-model="education.issuer"
                    class="form-field"
                    :rules="[rules.required]"
                    dense
                    label="University*"
                    placeholder="University"
                  />
                </v-col>
                <v-col class="education-modal__col">
                  <v-text-field
                    v-model="education.title"
                    class="form-field"
                    :rules="[rules.required]"
                    dense
                    label="Field of study*"
                    placeholder="Fields of study"
                  />
                </v-col>
              </v-row>

              <v-row class="education-modal__row">
                <v-col class="education-modal__col">
                  <v-menu
                    ref="startDate"
                    content-class="datepicker-menu"
                    :close-on-content-click="false"
                    transition="scale-transition"
                    offset-y
                  >
                    <template v-slot:activator="{ on }">
                      <div class="form-field--date-wrapper" v-on="on">
                        <v-text-field
                          :value="education.start_date"
                          class="form-field"
                          clearable
                          dense
                          label="Start date"
                          placeholder="Chose date"
                          readonly
                          v-on="on"
                          @click:clear="education.start_date = ''"
                        >
                          <template #append>
                            <v-icon color="blue">
                              mdi-calendar
                            </v-icon>
                          </template>
                        </v-text-field>
                      </div>
                    </template>
                    <v-date-picker
                      v-model="education.start_date"
                      type="month"
                      no-title
                      scrollable
                      @input="$refs.startDate.save(true)"
                    ></v-date-picker>
                  </v-menu>
                </v-col>

                <v-col class="education-modal__col">
                  <v-menu
                    ref="endDate"
                    content-class="datepicker-menu"
                    :close-on-content-click="false"
                    transition="scale-transition"
                    offset-y
                  >
                    <template v-slot:activator="{ on }">
                      <div class="form-field--date-wrapper" v-on="on">
                        <v-text-field
                          :value="localEndDate"
                          class="form-field"
                          clearable
                          dense
                          label="End date"
                          placeholder="Chose date"
                          readonly
                          v-on="on"
                          @click:clear="localEndDate = ''"
                        >
                          <template #append>
                            <v-icon color="blue">
                              mdi-calendar
                            </v-icon>
                          </template>
                        </v-text-field>
                      </div>
                    </template>
                    <v-date-picker
                      v-model="localEndDate"
                      type="month"
                      no-title
                      scrollable
                      @input="$refs.endDate.save(true)"
                    ></v-date-picker>
                  </v-menu>
                </v-col>
              </v-row>
            </template>

            <template v-else-if="selectedEducation.type === 'certificate'">
              <v-row class="education-modal__row">
                <v-col class="education-modal__col">
                  <v-text-field
                    v-model="education.title"
                    class="form-field"
                    :rules="[rules.required]"
                    dense
                    label="Name of the certificate*"
                    placeholder="Name of the certificate"
                  />
                </v-col>
                <v-col class="education-modal__col">
                  <v-text-field
                    v-model="education.issuer"
                    class="form-field"
                    :rules="[rules.required]"
                    dense
                    label="Certificate issuer*"
                    placeholder="Certificate issuer"
                  />
                </v-col>
              </v-row>
            </template>

            <template v-else-if="selectedEducation.type === 'course'">
              <v-row class="education-modal__row">
                <v-col class="education-modal__col">
                  <v-text-field
                    v-model="education.title"
                    class="form-field"
                    :rules="[rules.required]"
                    dense
                    label="Name of the course*"
                    placeholder="Name of the course"
                  />
                </v-col>
                <v-col class="education-modal__col">
                  <v-text-field
                    v-model="education.issuer"
                    class="form-field"
                    :rules="[rules.required]"
                    dense
                    label="Course issuer*"
                    placeholder="Course issuer"
                  />
                </v-col>
              </v-row>
            </template>
          </v-form>
        </v-card-text>

        <v-card-actions class="education-modal__actions">
          <v-row class="education-modal__row">
            <v-col class="education-modal__col"></v-col>
            <v-col class="education-modal__col text-right">
              <v-btn
                color="blue"
                width="100%"
                large
                dark
                :loading="isSubmitProcessing"
                @click="submit"
              >
                Save
              </v-btn>
            </v-col>
          </v-row>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import { mapState, mapActions } from 'vuex';
import validationRules from '@/helpers/validators';

const rules = validationRules;

export default {
  props: {
    value: {
      type: Boolean,
      required: true,
    },
    selectedEducation: {
      type: Object,
      required: true,
    },
  },

  data() {
    return {
      rules,
      isFormValid: true,
      localEndDate: '',
      education: {},
    };
  },

  computed: {
    ...mapState('hrDeveloper', [
      'developer',
      'isSubmitProcessing',
    ]),

    ...mapState('developer', { newDeveloper: 'developer' }),

    ...mapState('features', [
      'featuresData',
    ]),

    isVisible: {
      get() {
        return this.value;
      },
      set(value) {
        this.$emit('input', value);
      },
    },

    isRegistration() {
      return this.$route.name === 'developer-join';
    },
  },

  watch: {
    isVisible() {
      if (this.$refs.educationForm && this.isVisible) {
        this.$refs.educationForm.reset();
      }
      this.education = this.isVisible ? { ...this.selectedEducation } : {};
      this.localEndDate = this.education.end_date === 'ongoing' ? '' : this.education.end_date;
    },
  },

  methods: {
    ...mapActions('hrDeveloper', [
      'updateDeveloper',
    ]),

    ...mapActions('developer', [
      'addDeveloperData',
    ]),

    closeModal() {
      this.$emit('input', false);
    },

    submit() {
      this.$refs.educationForm.validate();

      if (this.isFormValid) {
        const newData = {};
        newData.education = this.isRegistration
          ? [...this.newDeveloper.education] : [...this.developer.education];


        if (this.selectedEducation.context === 'add') {
          this.education.end_date = !this.localEndDate ? 'ongoing' : this.localEndDate;
          newData.education.push(this.education);
        } else {
          newData.education[this.selectedEducation.index] = this.education;
          newData.education[this.selectedEducation.index].end_date = !this.localEndDate ? 'ongoing' : this.localEndDate;
        }

        newData.education.sort((a, b) => {
          if (a.start_date > b.start_date) return -1;
          if (a.start_date < b.start_date) return 1;
          return 0;
        });

        if (this.isRegistration) {
          this.addDeveloperData(newData)
            .then(() => {
              this.closeModal();
            });
        } else {
          this.updateDeveloper(newData)
            .then(() => {
              this.closeModal();
            });
        }
      }
    },
  },
};
</script>

<style lang="scss" scoped>
.education-modal {
  &__title {
    display: flex;
    justify-content: space-between;
    font-weight: 600 !important;
    font-size: 22px !important;
  }

  &__actions {
    padding: 16px 24px 16px 24px;
    justify-content: flex-end;
  }

  &__row {
    margin-right: -8px;
    margin-left: -8px;
  }

  &__col {
    padding: 0 8px 8px;
  }

  &__subtitle {
    color: $color--primary;
    font-size: 18px;
    line-height: 21px;
    font-weight: 400;
    margin-bottom: 8px;
  }
}
</style>
